import React from 'react';
import useSeo from '../lib/useSeo';
import useJsonLd from '../lib/useJsonLd';
import { Link } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import Reveal from '../components/site/Reveal';
import CountUp from '../components/site/CountUp';
import AALIGNMockup from '../components/site/AALIGNMockup';
import {
  Target,
  BarChart3,
  Workflow,
  ShieldCheck,
  Users2,
  MessageSquare,
  ChevronRight,
  ArrowRight,
  Check,
} from 'lucide-react';

const features = [
  {
    icon: Target,
    tag: 'AI scoring',
    title: 'Route only the leads worth pursuing',
    body:
      'AI-powered lead scoring learns from your own conversions and prioritises the deals with the highest chance of closing — so your team stops chasing the wrong opportunities.',
  },
  {
    icon: Workflow,
    tag: 'Workflows',
    title: 'Automated nurture, end-to-end',
    body:
      'Trigger emails, tasks, hand-offs, follow-ups and re-engagement plays automatically. AALIGN moves every lead through the funnel without a spreadsheet in sight.',
  },
  {
    icon: BarChart3,
    tag: 'Reporting',
    title: 'Real-time analytics and reporting',
    body:
      'See pacing, conversion, source performance and pipeline value live. Board-ready dashboards, exportable in one click, always tied to what the sales team actually did.',
  },
  {
    icon: Users2,
    tag: 'Collaboration',
    title: 'Team collaboration built in',
    body:
      'Shared queues, comments, hand-off notes, and role-based access. Your SDRs, AEs, marketing ops and campaign leads work from the same source of truth.',
  },
];

const workflowSteps = [
  { label: 'Capture', body: 'From forms, ads, integrations, syndication — every lead lands in one place with source captured.' },
  { label: 'Score', body: 'AI weighs firmographics, intent, engagement and history to rank each lead 0\u2013100.' },
  { label: 'Nurture', body: 'Automated sequences, personalised at scale, that adapt to opens, clicks and replies.' },
  { label: 'Convert', body: 'Sales-ready leads routed to the right rep with context, timing windows and next-best actions.' },
];

const security = [
  'GDPR compliant — consent captured, records auditable',
  'CCPA compliant — full data-subject rights honoured',
  'SOC 2 Type II — security controls independently audited',
  'Role-based access, encrypted at rest and in transit',
];

const stats = [
  { v: '5M+', k: 'Leads processed' },
  { v: '300+', k: 'Active users' },
  { v: '4.9/5', k: 'Customer rating' },
  { v: '247%', k: 'Higher conversion vs manual' },
];

const AALIGNPage = () => {
  useSeo({
    title: 'AALIGN — Audience alignment for direct-sold lead-gen',
    description: 'AALIGN matches publisher audiences to buyer intent in real time, so B2B media brands can sell direct campaigns with the confidence of a programmatic desk.',
    path: '/products/aalign',
  });
  useJsonLd([
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://fivedbs.com/products/aalign#service',
      name: 'AALIGN',
      alternateName: 'AALIGN by 5DBS',
      serviceType: 'B2B audience-to-buyer intent alignment',
      description: 'AALIGN maps a publisher\'s first-party audience to live buyer intent so direct-sold campaigns can be planned, priced and delivered with the confidence of a programmatic desk — while keeping every impression brand-safe.',
      provider: { '@id': 'https://fivedbs.com/#organization' },
      areaServed: [
        { '@type': 'Place', name: 'Global' },
        { '@type': 'Place', name: 'North America' },
        { '@type': 'Place', name: 'United Kingdom' },
        { '@type': 'Place', name: 'European Union' },
        { '@type': 'Place', name: 'APAC' },
      ],
      audience: {
        '@type': 'Audience',
        audienceType: 'B2B publishers running direct-sold advertising and lead-gen programmes',
      },
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        url: 'https://fivedbs.com/contact',
        priceCurrency: 'USD',
      },
      url: 'https://fivedbs.com/products/aalign',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://fivedbs.com/' },
        { '@type': 'ListItem', position: 2, name: 'Products', item: 'https://fivedbs.com/#products' },
        { '@type': 'ListItem', position: 3, name: 'AALIGN', item: 'https://fivedbs.com/products/aalign' },
      ],
    },
  ]);
  return (
    <div className="min-h-screen bg-white font-sans text-navy">
      <Navbar />
      <main>
        {/* Hero */}
        <section
          className="relative min-h-[70vh] flex items-center overflow-hidden pt-28"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(233,30,99,0.30) 0%, rgba(20,30,52,0.90) 60%, rgba(20,30,52,0.95) 100%), url('https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&q=80')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="relative max-w-6xl mx-auto w-full px-6 lg:px-8 py-20">
            <nav className="flex items-center gap-2 text-slate-300 text-[12px] font-bold uppercase tracking-[0.14em] mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight size={14} />
              <Link to="/#products" className="hover:text-white">Products</Link>
              <ChevronRight size={14} />
              <span className="text-white">AALIGN</span>
            </nav>
            <p className="text-brand-pink text-[12px] font-bold tracking-[0.16em] uppercase mb-5">
              Product · AALIGN
            </p>
            <h1 className="text-white font-black tracking-[-0.01em] leading-[1.05] text-[44px] sm:text-[58px] md:text-[68px] max-w-[900px]">
              Capture. Nurture. Convert.
            </h1>
            <p className="mt-7 text-slate-200/90 text-[17px] leading-[1.7] max-w-[720px]">
              The AI-powered lead management platform we license to publishers. AI lead scoring, real-time analytics, automated workflows, advanced reporting and team collaboration — built on enterprise-grade security.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="/#call"
                className="inline-flex items-center justify-center h-11 px-7 rounded-md bg-brand-pink hover:bg-brand-pink/90 text-white text-[13px] font-bold uppercase tracking-[0.08em] shadow-mk hover:shadow-mk-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                Book a demo
                <ArrowRight size={16} className="ml-2" />
              </a>
              <Link
                to="/#products"
                className="inline-flex items-center justify-center h-11 px-7 rounded-md bg-white/[0.06] backdrop-blur border border-white/25 text-white text-[13px] font-bold uppercase tracking-[0.08em] hover:bg-white/[0.12] transition-all duration-300"
              >
                Explore all products
              </Link>
            </div>
          </div>
        </section>

        {/* Machine-readable answer block — surfaces direct answers to "What is AALIGN?"
            style queries in AI answer engines (ChatGPT, Perplexity, Google AI Overviews). */}
        <section className="bg-white py-14 border-b border-slate-100">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 space-y-8">
            <div>
              <h2 className="text-navy font-black text-[22px] md:text-[26px] leading-[1.25]">What is AALIGN?</h2>
              <p className="mt-3 text-body-muted text-[15px] md:text-[16px] leading-[1.75]">AALIGN is 5DBS's audience-alignment engine that maps a B2B publisher's first-party audience to live buyer intent, so direct-sold campaigns can be scoped, priced and delivered with the confidence of a programmatic desk. It replaces the guesswork in classic direct sales with a real-time view of who inside a publisher's audience is currently in-market.</p>
            </div>
            <div>
              <h2 className="text-navy font-black text-[22px] md:text-[26px] leading-[1.25]">Who is AALIGN for?</h2>
              <p className="mt-3 text-body-muted text-[15px] md:text-[16px] leading-[1.75]">AALIGN is built for B2B publishers, ad-sales teams and media revenue leaders selling direct-sold advertising, lead-gen and content programmes. It fits any organisation that wants direct-sold economics with the audience precision buyers now expect from programmatic.</p>
            </div>
            <div>
              <h2 className="text-navy font-black text-[22px] md:text-[26px] leading-[1.25]">How does AALIGN work?</h2>
              <p className="mt-3 text-body-muted text-[15px] md:text-[16px] leading-[1.75]">AALIGN ingests the publisher's first-party signals — content engagement, form submissions, subscription data — and joins them to 5DBS's buyer graph and third-party intent feeds. Every audience segment gets a live "match score" against a buyer's ICP, which the sales team uses to shape proposals, guarantees and CPL pricing.</p>
            </div>
          </div>
        </section>

        {/* Stats band */}
        <section className="bg-brand-soft py-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <Reveal key={s.k} delay={i * 90} className="text-center">
                <div className="text-brand-pink text-[36px] sm:text-[42px] font-black leading-none tracking-tight">
                  <CountUp value={s.v} />
                </div>
                <p className="mt-3 text-body-muted text-[13.5px] font-semibold">{s.k}</p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Product mockup */}
        <section className="bg-white py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-10">
              <p className="text-brand-pink text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
                Inside the workspace
              </p>
              <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.12] text-[32px] sm:text-[40px] md:text-[46px]">
                Every lead, scored and routed
              </h2>
              <p className="mt-5 text-body-muted text-[16px] leading-[1.75]">
                A live view of what your team lands in every morning — pipeline, scores, statuses and next best actions, all in one place.
              </p>
            </div>
            <Reveal className="max-w-5xl mx-auto">
              <AALIGNMockup />
              <p className="mt-4 text-center text-body-muted text-[13px] italic">
                Sample workspace — pipeline metrics, scored leads and one-click routing.
              </p>
            </Reveal>
          </div>
        </section>

        {/* Features */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-brand-pink text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
                What it does
              </p>
              <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.12] text-[32px] sm:text-[40px] md:text-[46px]">
                Everything a modern lead-gen team needs, in one place
              </h2>
            </div>

            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((f, i) => {
                const Icon = f.icon;
                return (
                  <Reveal
                    key={f.title}
                    delay={i * 110}
                    className="bg-white rounded-2xl shadow-mk p-7 flex gap-5 hover:shadow-mk-lg hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="shrink-0 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-brand-pink to-brand-pink/70 text-white shadow-[0_4px_11px_0_rgba(233,30,99,0.35)]">
                      <Icon size={22} strokeWidth={2.2} />
                    </div>
                    <div>
                      <span className="inline-block text-brand-pink text-[10.5px] font-bold uppercase tracking-[0.14em] bg-brand-pink/10 rounded-full px-2.5 py-0.5">
                        {f.tag}
                      </span>
                      <h3 className="mt-3 text-navy text-[19px] font-bold">{f.title}</h3>
                      <p className="mt-2 text-body-muted text-[14.5px] leading-[1.7]">{f.body}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Workflow */}
        <section className="bg-brand-soft py-20 md:py-24">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-brand-pink text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
                The workflow
              </p>
              <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.12] text-[32px] sm:text-[40px] md:text-[46px]">
                Capture → Score → Nurture → Convert
              </h2>
            </div>
            <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-5">
              {workflowSteps.map((s, i) => (
                <Reveal
                  key={s.label}
                  delay={i * 120}
                  className="relative bg-white rounded-2xl shadow-mk p-6 pt-14 hover:shadow-mk-lg hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="absolute -top-6 left-6 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-brand-pink to-brand-pink/70 text-white shadow-[0_4px_11px_0_rgba(233,30,99,0.35)] font-black text-[15px]">
                    0{i + 1}
                  </div>
                  <h3 className="text-navy text-[18px] font-bold">{s.label}</h3>
                  <p className="mt-2 text-body-muted text-[13.5px] leading-[1.7]">{s.body}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Security */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <p className="text-brand-pink text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
                Enterprise-grade
              </p>
              <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.12] text-[30px] sm:text-[38px]">
                Security your CISO can sign off in one meeting
              </h2>
              <p className="mt-5 text-body-muted text-[15.5px] leading-[1.75]">
                AALIGN is built for regulated markets from the first line of code — not bolted on after the sale.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {security.map((s, i) => (
                <Reveal
                  key={s}
                  delay={i * 90}
                  className="flex items-center gap-3 rounded-2xl bg-brand-soft border border-slate-100 px-5 py-4"
                >
                  <span className="h-9 w-9 rounded-xl bg-gradient-to-br from-brand-pink to-brand-pink/70 text-white flex items-center justify-center shrink-0 shadow-[0_4px_11px_0_rgba(233,30,99,0.35)]">
                    <Check size={16} strokeWidth={3} />
                  </span>
                  <span className="text-navy text-[13.5px] font-bold">{s}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section
          className="relative py-24"
          style={{ background: 'linear-gradient(135deg, #1A2035 0%, #344767 100%)' }}
        >
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <MessageSquare size={28} className="mx-auto text-brand-pink mb-4" />
            <h2 className="text-white font-black tracking-[-0.01em] leading-[1.15] text-[30px] sm:text-[40px]">
              See AALIGN running your kind of campaigns
            </h2>
            <p className="mt-5 text-slate-300 text-[16px] leading-[1.7]">
              Bring one campaign spec and one advertiser. We'll wire it into a live AALIGN workspace during the demo.
            </p>
            <div className="mt-9">
              <a
                href="/#call"
                className="inline-flex items-center justify-center h-12 px-8 rounded-md bg-brand-pink hover:bg-brand-pink/90 text-white text-[13px] font-bold uppercase tracking-[0.08em] shadow-mk hover:shadow-mk-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                Book a demo
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AALIGNPage;
